import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../../api.js';
import { useAuth } from '../../auth.jsx';
import Alert from '../../components/ui/Alert.jsx';
import { Loading, PageHeader, Section } from '../../components/ui/index.jsx';

// Three layers keep a scam's HTML harmless here:
//   1. the worker already removed scripts, frames, forms, handlers, link targets and
//      remote images (sanitizeEmailHtml);
//   2. the iframe is sandbox="" — no scripts, no forms, no navigation, its own empty origin;
//   3. the document carries its own Content-Security-Policy: nothing may load from anywhere.
const CSP = "default-src 'none'; img-src data:; style-src 'unsafe-inline'; font-src data:; form-action 'none'; base-uri 'none'";

const wrap = (html) => `<!doctype html><html><head><meta charset="utf-8">
<meta http-equiv="Content-Security-Policy" content="${CSP}">
<style>body{margin:16px;font-family:Arial,sans-serif;color:#1d2433;overflow-wrap:anywhere}
a[data-link]{color:#1d4ed8}.ss-blocked-image{display:inline-block;padding:2px 6px;border:1px dashed #c2cad6;color:#56637a;font-size:12px}</style>
</head><body>${html}</body></html>`;

/** The original email as it looked — safely: nothing in it can run, load or be clicked. */
export default function InboxView({ box = 'inbox' }) {
  const { messageId } = useParams();
  const { organisation } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!organisation) return;
    api(`/portal/organisations/${organisation.id}/${box}/${messageId}/html`).then(setData).catch((err) => setError(err.message));
  }, [organisation, messageId, box]);

  const back = { to: `/${box}/${messageId}`, label: 'The email' };
  if (error) return <div className="page"><PageHeader back={back} title="As it looked" /><Alert tone="danger">{error}</Alert></div>;
  if (!data) return <div className="page"><Loading /></div>;

  return (
    <div className="page">
      <PageHeader back={back} title="As it looked" subtitle={data.subject || '(no subject)'} />
      <Alert tone="info">
        Shown safely: nothing in it can run, load from the internet or be clicked. Images from the internet are left out
        (scammers use them to tell when an email is opened). On a computer, point at a link to see where it goes.
      </Alert>
      <Section flush>
        <iframe className="email-frame" title="The original email" sandbox="" referrerPolicy="no-referrer" srcDoc={wrap(data.html)} />
      </Section>
    </div>
  );
}
